import { humanPopulation } from "src/components/ui";
import type { EngagementStatus, Population } from "src/graphql/generated";

/**
 * The Status filter's options: groups of derived positions rather than the four
 * positions themselves.
 *
 * "Hired" is one option over both not-yet-working states. Whether a start date has
 * been agreed is not what somebody narrowing this list is asking — they want the
 * people who are signed and not working yet — and `IdentityFilter.statuses` is a
 * list, so the grouping asks nothing of the backend. The enum still has four
 * members, and the server owns their derivation.
 *
 * The row badges deliberately do *not* group: a missing start with a recorded end
 * differs from a future start. A term with neither date and no termination is
 * ACTIVE instead, so it belongs under Active even though its start is unknown.
 * The field's hint explains why Hired covers two badges.
 *
 * Lives here rather than in the screen so the labels have one home: the `<option>`
 * list and the filter summary below both read them, and a summary that spelled
 * "Ended" out again could drift from the option a reader actually picked.
 */
export const statusFilters: { value: string; label: string; statuses: EngagementStatus[] }[] = [
  { value: "HIRED", label: "Hired", statuses: ["NOT_STARTED", "UPCOMING"] },
  { value: "ACTIVE", label: "Active", statuses: ["ACTIVE"] },
  { value: "ENDED", label: "Ended", statuses: ["ENDED"] }
];

/**
 * The two checkboxes' labels, keyed by the control each one belongs to.
 *
 * Keyed by control rather than by text because the two diverge on purpose:
 * `currentOnly` is the name `IdentityFilter.currentOnly` gives the wire field, and
 * "Not closed out" is what the box tests — `terminated_on IS NULL`, nothing else.
 * Naming the key after the label instead would leave a constant called
 * `notClosedOut` holding the *other* box's text.
 *
 * One home for the same reason `statusFilters` has one: the screen renders these
 * and the summary below repeats them, and the summary drifting from the control it
 * quotes is the defect this module exists to prevent — the version of it that
 * shipped as a wrong label is what INFRA-695 was.
 */
export const filterLabels = {
  currentOnly: "Not closed out",
  notClosedOut: "Ended, not closed out"
} as const;

/** The filter controls' state, as the People screen holds it. */
export type FilterControls = {
  readonly population: Population | "";
  readonly status: string;
  readonly search: string;
  readonly currentOnly: boolean;
  readonly notClosedOut: boolean;
};

/**
 * The filters actually narrowing the list, each named as its own control names it.
 *
 * Why this exists: a tick left on from an earlier search is invisible once the
 * reader has scrolled past it, and the Status select and "Not closed out" neither
 * clear nor disable each other. So an operator who set one, then changed the other,
 * has silently asked a different question than the one they think they asked — and
 * the list gave them a straight answer to it. That is what happened in the report
 * this closes: Ended *plus a leftover tick* returned one person, and the conclusion
 * drawn was that the filters were broken.
 *
 * Read off the *effective* filter rather than the widget state, which is the only
 * version worth showing. "Ended, not closed out" overrides both of its neighbours —
 * the screen sends `["ENDED"]` and `currentOnly` regardless of what they hold — so
 * while it is ticked a stale Status is not merely invisible, it is inert. Listing it
 * would name a filter that is doing nothing.
 */
export function describeActiveFilters(controls: FilterControls): string[] {
  const parts: string[] = [];
  if (controls.population) {
    parts.push(`Population: ${humanPopulation(controls.population)}`);
  }
  if (controls.notClosedOut) {
    parts.push(filterLabels.notClosedOut);
  } else {
    const status = statusFilters.find((f) => f.value === controls.status);
    if (status) {
      parts.push(`Status: ${status.label}`);
    }
    if (controls.currentOnly) {
      parts.push(filterLabels.currentOnly);
    }
  }
  const search = controls.search.trim();
  if (search) {
    parts.push(`Search: “${search}”`);
  }
  return parts;
}

/**
 * The one thing the controls cannot say for themselves: that two of them combine
 * into a narrower question than either looks like on its own.
 *
 * Both conditions have to hold of the *same* engagement — `store.go`'s single EXISTS
 * subquery ANDs them, and its own comment says that is the point: "active and already
 * started" must mean one engagement that is both, not a person with an active term
 * somewhere and a started one elsewhere. A reader who assumes the two are asked of
 * the *person* expects a union and gets an intersection.
 *
 * Ended is called out by name because that pair is not a rare accident, it is the
 * reported one, and the console already ships the same question under a name that
 * says what it is. Pointing at that control is the useful thing to say: it tells a
 * reader who arrived here by a stale tick that what they have built has a name, and
 * where it is.
 *
 * Still deliberately structural rather than explanatory: it says how the two combine
 * and where the named version lives, and nothing about what either one means. It used
 * to owe the reader a paraphrase because the box was called "Current term only" while
 * testing `terminated_on IS NULL` — but that gap was in the label, not in this note,
 * and INFRA-695 closed it by renaming the control to what it tests. A control that
 * states its own predicate needs no gloss here.
 */
export function describeFilterCombination(controls: FilterControls): string | null {
  // Its own label already names the combination it stands for, and it has overridden
  // the two controls this note would otherwise be about.
  if (controls.notClosedOut) {
    return null;
  }
  const status = statusFilters.find((f) => f.value === controls.status);
  if (!status || !controls.currentOnly) {
    return null;
  }
  const base = `Status “${status.label}” and “${filterLabels.currentOnly}” both have to be true of the same engagement — not of the same person.`;
  if (status.value !== "ENDED") {
    return base;
  }
  return `${base} That pair is what the “${filterLabels.notClosedOut}” box asks on its own.`;
}

/**
 * The second thing the Status control cannot say for itself: it is asked of a person's
 * whole history, while the Engagements column shows where they stand now.
 *
 * Those are different terms, and they can disagree. Somebody let go in 2024 and rehired
 * in 2026 is returned by Status “Ended” — the 2024 term matches — and their row reads
 * “Active”, because that is where they stand today. The column used to print a chip per
 * term, so the matching one was on screen somewhere among the undated repeats; since it
 * collapses to one entry per population, the match is carried by the count of earlier
 * terms beside the badge rather than by a chip of its own.
 *
 * Silent whenever the two cannot disagree, which is most of the time: “Not closed out”
 * ticked alongside, or the “Ended, not closed out” box, makes the matching term one
 * nobody has terminated, and that is precisely the term the column shows. Saying it
 * there would be a warning about a contradiction that cannot arise.
 */
export function describeStatusScope(controls: FilterControls): string | null {
  if (controls.notClosedOut || controls.currentOnly) {
    return null;
  }
  const status = statusFilters.find((f) => f.value === controls.status);
  if (!status) {
    return null;
  }
  return `Status “${status.label}” matches any term in a person's history. A row's Engagements column shows where they stand now, with the terms behind it counted beside the badge.`;
}
